import { IPromise, IQService } from 'angular';
import {
    FontStyleService,
    IFontStyleModel,
    hasOwnProperty,
    isIFontStyle,
} from '../../tn.common';
import DjangoClient from '../../tn.django/django-client.service/djangoClient';

export interface INewDjangoFavorite {
    name: string;
    style: number[];
}

export interface IDjangoFavorite extends INewDjangoFavorite {
    id: number;
    user: number;
    created?: string;
}

export interface INewFavoriteList extends Omit<INewDjangoFavorite, 'style'> {
    style: IFontStyleModel[];
}

export interface IFavoriteList extends Omit<IDjangoFavorite, 'style'> {
    date?: Date;
    style: IFontStyleModel[];
    styleIds: number[];
}

export function isINewFavoriteList(obj: unknown): obj is INewFavoriteList {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        (!hasOwnProperty(obj, 'id') || obj.id === undefined) &&
        hasOwnProperty(obj, 'style') &&
        Array.isArray(obj.style) &&
        obj.style.every((s) => isIFontStyle(s))
    );
}

export function isIFavoriteList(obj: unknown): obj is IFavoriteList {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'id' in obj &&
        'user' in obj &&
        hasOwnProperty(obj, 'name') &&
        !!obj.name &&
        'created' in obj &&
        hasOwnProperty(obj, 'style') &&
        Array.isArray(obj.style) &&
        obj.style.every((s) => isIFontStyle(s))
    );
}

export const NEW_FAVORITE_LIST: INewFavoriteList = {
    style: [],
    name: '',
};

export class FavoritesFilters {
    name?: string;
    date?: {
        start: { month?: number; year?: number };
        end: { month?: number; year?: number };
    };
}

export function filterFavorite(
    fav: IFavoriteList,
    filters: FavoritesFilters
): boolean {
    if (filters.name) {
        const needle = filters.name.toLowerCase();
        if (
            !fav.name.toLowerCase().includes(needle) &&
            !fav.style.some((style) =>
                style.name.toLowerCase().includes(needle)
            )
        ) {
            return false;
        }
    }

    if (filters.date?.start?.year) {
        const month = filters.date?.start?.month || 1;
        const year = filters.date?.start?.year;
        if (month && year) {
            const startDate = new Date(year, month - 1);
            if (startDate && fav.date && startDate > fav.date) {
                return false;
            }
        }
    }

    if (filters?.date?.end?.year) {
        const month = filters.date?.end?.month || 12;
        const year = filters.date?.end?.year;
        if (month && year) {
            const endDate = new Date(year, month - 1);
            if (endDate && fav.date && endDate < fav.date) {
                return false;
            }
        }
    }
    return true;
}

export class FavoritesRepository {
    static $inject = ['djangoClient', '$q', 'FontStyleService'];
    constructor(
        protected djangoClient: DjangoClient,
        protected $q: IQService,
        protected styleService: FontStyleService
    ) {}

    listFavorites(): IPromise<IFavoriteList[]> {
        return this.djangoClient
            .getAllPages<IDjangoFavorite>('/api/1/favorites/')
            .then((data) => data.results)
            .then((data) => this._transformFavsToClient(data));
    }

    getFavoriteList(id: number): IPromise<IFavoriteList> {
        return this.djangoClient
            .get<IDjangoFavorite>(`/api/1/favorites/${id}/`)
            .then((data) => this._transformFavToClient(data));
    }

    emailFavoriteList(email: string, id: number): IPromise<unknown> {
        return this.djangoClient.post(`/api/1/favorites/${id}/email/${email}/`);
    }

    deleteFavoriteList(id: number): IPromise<unknown> {
        return this.djangoClient.del(`/api/1/favorites/${id}/`);
    }

    saveFavoriteList(
        input: INewFavoriteList | IFavoriteList
    ): IPromise<IFavoriteList> {
        if (isINewFavoriteList(input)) {
            const favList = this._transformNewFavToServer(input);
            const url = '/api/1/favorites/';
            return this.djangoClient
                .post<IDjangoFavorite, INewDjangoFavorite>(url, favList)
                .then((djangoFav) => this._transformFavToClient(djangoFav));
        }
        const favList = this._transformFavsToServer(input);
        const url = `/api/1/favorites/${favList.id}`;
        return this.djangoClient
            .put<IDjangoFavorite>(url, favList)
            .then((djangoFav) => this._transformFavToClient(djangoFav));
    }

    _transformNewFavToServer(favList: INewFavoriteList): INewDjangoFavorite {
        const style = favList.style.map((s) => s.id);
        return { ...favList, style };
    }

    _transformFavsToServer(favList: IFavoriteList): IDjangoFavorite {
        const style = favList.style.map((s) => s.id);
        const djangoFavorite: IDjangoFavorite = { ...favList, style };
        return djangoFavorite;
    }

    _transformFavsToClient(
        favList: IDjangoFavorite[]
    ): IPromise<IFavoriteList[]> {
        return this.$q.all(favList.map((f) => this._transformFavToClient(f)));
    }
    _transformFavToClient(favList: IDjangoFavorite): IPromise<IFavoriteList> {
        const date =
            (favList.created && new Date(favList.created)) || undefined;
        const styleIds = [...favList.style];
        return this.styleService
            .list({ id: favList.style, page_size: favList.style.length + 1 })
            .then(({ results: resolvedStyles }) => {
                const style = resolvedStyles;
                const familiesSet = new Set(
                    resolvedStyles.map((s) => s.family)
                );
                const families = Array.from(familiesSet);
                const favoriteList = Object.assign({}, favList, {
                    date,
                    styleIds,
                    style,
                    families,
                });
                return favoriteList;
            });
    }
}
